import React from "react";
import fitty from "fitty";

interface FittyTextProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  minSize?: number;
  maxSize?: number;
  wrapText?: boolean;
  wrapperStyle?: React.CSSProperties;
  wrapperClassName?: string;
}

const defaultStyles = { width: "100%" };

export const FittyText = React.forwardRef<HTMLDivElement, FittyTextProps>(
  function FittyText(
    {
      children,
      minSize = 12,
      maxSize = 512,
      wrapText = false,
      style,
      className,
      wrapperStyle,
      wrapperClassName,
      ...rest
    },
    ref,
  ) {
    const internalRef = React.useRef<HTMLDivElement>(null);
    const correctRef =
      (ref as React.MutableRefObject<HTMLDivElement>) || internalRef;

    const fittyConfig = React.useMemo(
      () => ({
        minSize,
        maxSize,
        multiLine: wrapText,
        observeMutations: {
          subtree: true,
          childList: true,
          characterData: true,
          attributeFilter: ["class"],
        },
      }),
      [minSize, maxSize, wrapText],
    );

    React.useLayoutEffect(() => {
      const effectRef = correctRef.current;
      if (!effectRef) {
        console.warn("FittyText: No ref available");
        return;
      }

      const fitInstance = fitty(effectRef, fittyConfig);

      // Delay fit calculation to next frame
      const timeoutId = setTimeout(() => {
        fitInstance.fit();
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        fitInstance.unsubscribe();
      };
    }, [correctRef, fittyConfig]);

    return (
      <div
        style={{ ...defaultStyles, ...wrapperStyle }}
        className={wrapperClassName}
      >
        <div {...rest} ref={correctRef} style={style} className={className}>
          {children}
        </div>
      </div>
    );
  },
);

FittyText.displayName = "FittyText";
